import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronRight, PencilLine, Send, Trash2 } from "lucide-react";
import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { CollapsibleAssignedTableBody } from "./AssignedTableCollapsible";

import EmptyData from "@/components/state-pages/EmptyData";
import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Form } from "@/components/ui/form";
import { EmployeeErrorMessage } from "@/constants/employees/employee.constants";
import { DELETE_CONFIRMATION_CONSTANTS } from "@/constants/global-constants";
import { PROJECT_CONSTANTS } from "@/constants/projects.constant";
import {
  IProjectAssignedTool,
  ProjectToolStatus,
  ToolStatus,
} from "@/interfaces/tools.interface";
import {
  useDeleteAssignedToolsMutation,
  useSendToSiteAssignedToolsMutation,
  useUpdateAssignedToolsMutation,
} from "@/redux/api/projectsQuery";
import { toolsApi, useGetSubProductListQuery } from "@/redux/api/toolsQuery";
import { showAlertDialog } from "@/redux/slices/AlertSlice";
import {
  TUpdateAwaitingTools,
  updateAwaitingToolsSchema,
} from "@/schemas/tools.schema";
import { customToast } from "@/utils/custom-toast";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";
import { validateResponse } from "@/utils/helper-functions/form.helper";

interface AssignedTableProps {
  data: IProjectAssignedTool[];
}

export type TEditData = {
  projectDetailId: string;
  productId: string;
  id: string;
  parentIndex: number;
};

const AssignedTable = ({ data }: AssignedTableProps): JSX.Element => {
  const { id: projectId } = useParams();
  const [editData, setEditData] = React.useState<TEditData | null>(null);
  const dispatch = useDispatch();

  const { TOAST_MESSAGE, CONTENT_TEXT } =
    PROJECT_CONSTANTS.ASSIGN_TOOLS_CONSTANTS;

  const [updateAssignedTools, { isLoading: isUpdating }] =
    useUpdateAssignedToolsMutation();
  const [deleteAssignedTools] = useDeleteAssignedToolsMutation();

  const [sendToSite] = useSendToSiteAssignedToolsMutation();

  const { data: singleProductData, isLoading } = useGetSubProductListQuery(
    {
      productId: editData?.productId || "",
      status: ToolStatus.AVAILABLE,
    },
    {
      skip: !editData?.productId,
    },
  );

  const fetchedSingleProduct = singleProductData?.data;

  const form = useForm<TUpdateAwaitingTools>({
    resolver: zodResolver(updateAwaitingToolsSchema),
    ...(data && {
      values: {
        items: data?.map((item) => ({
          productDetailId: item?.id,
          productAssignments: item?.productAssignments?.map((detail) => ({
            id: detail.id,
            quantity:
              detail.assignedQuantity ||
              detail.subProductAssignments.length ||
              0,
            description: detail?.description || "",
            note: detail?.note || undefined,

            type: detail?.product?.type || "",
            subProductIds: detail?.subProductAssignments?.map(
              (subProductAssignment) =>
                subProductAssignment?.subProduct.id || "",
            ),

            isAltered: false,
          })),
        })),
      },
    }),
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "items",
    keyName: "idx",
  });

  const handleDelete = (productAssignmentId: string): void => {
    dispatch(
      showAlertDialog({
        title: DELETE_CONFIRMATION_CONSTANTS.title,
        desc: "If you delete this you cannot restore this option",
        btnName: DELETE_CONFIRMATION_CONSTANTS.btnName,
        cancelName: DELETE_CONFIRMATION_CONSTANTS.cancelName,
        btnAction: async () => {
          const response = await deleteAssignedTools({
            id: productAssignmentId,
            projectId: projectId!,
          });
          validateResponse({
            response,
            onSuccess: () => {
              customToast.success(TOAST_MESSAGE.PRODUCT_REMOVED_SUCCESSFULLY);
            },
          });
        },
      }),
    );
  };

  const handleSendToSite = (productAssignmentBatchId: string): void => {
    dispatch(
      showAlertDialog({
        title: CONTENT_TEXT.SENT_TO_SITE_CONFIRMATION_TITLE,
        desc: CONTENT_TEXT.SENT_TO_SITE_CONFIRMATION,
        btnName: CONTENT_TEXT.CONFIRMATION_BTN,
        cancelName: DELETE_CONFIRMATION_CONSTANTS.cancelName,
        btnAction: async () => {
          const response = await sendToSite({
            body: { status: ProjectToolStatus.AWAITING },
            productAssignmentBatchId,
            projectId: projectId!,
          });
          validateResponse({
            response,
            onSuccess: () => {
              customToast.success(TOAST_MESSAGE.SENT_TO_SITE_SUCCESSFULLY);
            },
          });
        },
      }),
    );
  };

  const actions = (props: TEditData): ActionButtonDataTypes[] => [
    {
      icon: PencilLine,
      name: "Edit",
      onclick: (): void => {
        setEditData(props);
      },
    },
    {
      icon: Trash2,
      name: "Remove Product",
      isTrash: true,
      onclick: (): void => {
        handleDelete(props?.projectDetailId);
      },
    },
  ];

  const handleUpdateTools = async (
    data: TUpdateAwaitingTools,
  ): Promise<void> => {
    const editedProductCard = data?.items[editData?.parentIndex as number];
    if (!editedProductCard) {
      customToast.error(EmployeeErrorMessage.GLOBAL_ERROR);

      return;
    }

    const alteredProduct = editedProductCard?.productAssignments?.find(
      (detail) => detail?.id === editData?.id,
    );

    if (!alteredProduct) {
      customToast.error(EmployeeErrorMessage.GLOBAL_ERROR);

      return;
    }

    const response = await updateAssignedTools({
      body: { ...alteredProduct, productAssignmentId: alteredProduct?.id },
      id: editData?.projectDetailId ?? "",
      projectId: projectId!,
    });

    validateResponse({
      response,
      onSuccess: () => {
        dispatch(toolsApi.util.invalidateTags(["subProductList"]));
        customToast.success(TOAST_MESSAGE.TOOLS_UPDATED);
        setEditData(null);
      },
    });
  };

  return (
    <div className='mt-2'>
      <Form {...form}>
        <section className=' space-y-7'>
          {fields?.length ? (
            fields?.map((field, parentIndex) => {
              const selectedProductDetail = data?.find(
                (item) => item?.id === field?.productDetailId,
              );

              return (
                <Collapsible
                  defaultOpen={parentIndex === 0 || parentIndex === 1}
                  className='group'
                  key={field.idx}
                >
                  <div className='flex justify-between items-center space-x-2 group-data-[state=closed]:border group-data-[state=closed]:rounded-md  group-data-[state=closed]:p-3 '>
                    <CollapsibleTrigger className='transition-transform'>
                      <div className='flex'>
                        <ChevronRight
                          className='min-w-5 min-h-5 mr-1 group-data-[state=open]:rotate-90 '
                          strokeWidth={2}
                        />
                        <h2 className='text-lg font-medium'>
                          {dateFormat(
                            selectedProductDetail?.createdAt,
                            "dd MMM yyyy | p",
                          )}
                        </h2>
                      </div>
                    </CollapsibleTrigger>
                    <div className='flex gap-4 items-center group-data-[state=open]:mb-3'>
                      <ButtonWithIcon
                        icon={
                          <Send className='size-4  mr-2' strokeWidth={2.5} />
                        }
                        variant={"light"}
                        onClick={() => {
                          if (
                            !selectedProductDetail?.productAssignments?.length
                          ) {
                            customToast.error(
                              TOAST_MESSAGE.NO_PRODUCTS_ASSIGNED,
                            );

                            return;
                          }
                          handleSendToSite(selectedProductDetail?.id ?? "");
                        }}
                      >
                        Sent To Site
                      </ButtonWithIcon>
                    </div>
                  </div>
                  <CollapsibleContent>
                    <CollapsibleAssignedTableBody
                      actions={actions}
                      isLoading={isLoading}
                      editData={editData}
                      fetchedSingleProduct={fetchedSingleProduct}
                      parentIndex={parentIndex}
                      handleUpdateTools={handleUpdateTools}
                      isUpdating={isUpdating}
                      selectedProductDetail={selectedProductDetail}
                    />
                  </CollapsibleContent>
                </Collapsible>
              );
            })
          ) : (
            <EmptyData title='Assigned Tools' />
          )}
        </section>
      </Form>
    </div>
  );
};

export default AssignedTable;
